<template>
  <div class="col-12">
    <div class="card" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ t('assignments.submit_assignment_title') }}
            </h6>
          </div>
        </div>
      </div>
   
      <div class="card-body px-0 pb-2 mx-2"> 
        <div class="row justify-content-center">
          <div class="col-12">
            <form @submit.prevent="handleSubmit">
              <div class="row g-3">
                <!-- Display Student Info (readonly) -->
                <div class="col-12 col-lg-4">
                  <label class="bmd-label-float">{{ t('assignments.student') }}</label>
                  <input
                    type="text"
                    class="form-control borderless-input"
                    :value="studentName"
                    readonly
                  />
                </div>

                <!-- Display Class Info (readonly) -->
                <div class="col-12 col-lg-3">
                  <label class="bmd-label-float">{{ t('assignments.class') }}</label>
                  <input
                    type="text"
                    class="form-control borderless-input"
                    :value="studentClass"
                    readonly
                  />
                </div>

                <!-- Display Group Info (readonly) -->
                <div class="col-12 col-lg-3">
                  <label class="bmd-label-float">{{ t('assignments.group') }}</label>
                  <input
                    type="text"
                    class="form-control borderless-input"
                    :value="studentGroup"
                    readonly
                  />
                </div>

                <!-- Assignment Title -->
                <div class="col-12 col-lg-6">
                  <label class="bmd-label-float">{{ t('assignments.assignment') }}</label>
                  <input
                    type="text"
                    class="form-control borderless-input"
                    :value="assignmentTitle"
                    readonly
                  />
                </div>

                <!-- File Upload -->
                <div class="col-12 col-lg-12">
                  <label for="file" class="bmd-label-float">{{ t('assignments.assignment_file') }}</label>
                  <input
                    type="file"
                    class="form-control borderless-input"
                    id="file"
                    ref="fileInput"
                    :class="{ 'is-invalid': errors.file }"
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.txt"
                    required
                    @change="handleFileChange"
                  />
                  <div class="invalid-feedback">{{ errors.file }}</div>
                </div>

                <!-- Buttons -->
                <div class="col-12">
                  <div class="d-flex flex-column flex-lg-row gap-3 mt-3">
                    <button
                      type="submit"
                      class="btn btn-primary btn-sm flex-grow-1"
                      :disabled="loading"
                    >
                      <span v-if="loading">
                        <i class="fas fa-spinner fa-spin ms-2"></i>
                        {{ t('assignments.saving') }}
                      </span>
                      <span v-else>
                        <i class="fas fa-save ms-2"></i>
                        {{ t('assignments.submit_assignment') }}
                      </span>
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-secondary btn-sm flex-grow-1"
                      @click="handleCancel"
                      :disabled="loading"
                    >
                      <i class="fas fa-times ms-2"></i>
                      {{ t('assignments.cancel') }}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import api from '@/api';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const toast = useToast();
const fileInput = ref(null);

const loading = ref(false);
const assignmentId = ref(route.params.assignmentId);
const assignmentTitle = ref('');
const studentInfo = ref({});
const studentClass = ref('');
const studentGroup = ref('');
const studentName = ref('');

const formData = ref({
  file: null
});

const errors = ref({});

const fetchStudentInfo = async () => {
  try {
    const response = await api.get('/student/profile');
    studentInfo.value = response.data.data;
    
    studentName.value = `${studentInfo.value.first_name} ${studentInfo.value.father_name} ${studentInfo.value.last_name}`;
    
    if (studentInfo.value.class_groups && studentInfo.value.class_groups.length > 0) {
      const currentGroup = studentInfo.value.class_groups[0];
      studentClass.value = currentGroup.class?.name || t('assignments.not_specified');
      studentGroup.value = currentGroup.name || t('assignments.not_specified');
    }
  } catch (error) {
    toast.error(t('assignments.load_failed_student'));
    console.error(error);
  }
};

const fetchAssignment = async () => {
  try {
    const response = await api.get(`/assignments/${assignmentId.value}`);
    assignmentTitle.value = response.data.data.title;
  } catch (error) {
    toast.error(t('assignments.load_failed_assignment'));
    console.error(error);
  }
};

const handleFileChange = (event) => {
  formData.value.file = event.target.files[0];
};

const validateForm = () => {
  errors.value = {};
  let isValid = true;
  
  if (!formData.value.file) {
    errors.value.file = t('assignments.file_required');
    isValid = false;
  }
  
  return isValid;
};

const handleSubmit = async () => {
  if (!validateForm()) {
    toast.warning(t('assignments.attach_assignment_file'));
    return;
  }

  try {
    loading.value = true;
    
    const formDataToSend = new FormData();
    formDataToSend.append('file', formData.value.file);
    formDataToSend.append('student_id', studentInfo.value.id); // Add student ID

    // Use the correct endpoint from your backend
    await api.post(`/assignments/${assignmentId.value}/submissions`, formDataToSend, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    toast.success(t('assignments.submit_success'));
    router.push({ name: 'student.assignments' }); // Navigate to assignments list
  } catch (error) {
    if (error.response?.data?.errors) {
      errors.value = error.response.data.errors;
      toast.error(error.response.data.message || t('assignments.submit_failed'));
    } else {
      toast.error(t('assignments.submit_error'));
    }
    console.error(error);
  } finally {
    loading.value = false;
  }
};

const handleCancel = () => {
  router.push({ name: 'student.assignments' });
};

onMounted(() => {
  fetchStudentInfo();
  fetchAssignment();
});
</script>